import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import AdminRadar from "@/pages/AdminRadar";
import AdminMessages from "@/pages/AdminMessages";
import AdminScience from "@/pages/AdminScience";
import AdminSiteReview from "@/pages/AdminSiteReview";

const emptyForm = { title: "", excerpt: "", body: "", published: true };

const AdminDashboard = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [texts, setTexts] = useState([]);
    const [form, setForm] = useState(emptyForm);
    const [editingId, setEditingId] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [tab, setTab] = useState("texts");

    const loadTexts = async () => {
        const r = await api.get("/texts/admin/all");
        setTexts(r.data);
    };

    useEffect(() => {
        let cancelled = false;
        api.get("/auth/me")
            .then((r) => {
                if (!cancelled) setUser(r.data);
                return loadTexts();
            })
            .catch(() => {
                navigate("/admin/login", { replace: true });
            });
        return () => {
            cancelled = true;
        };
    }, [navigate]);

    const handleLogout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (_err) {
            // ignore — o navigate abaixo já tira o usuário do painel
        }
        navigate("/admin/login", { replace: true });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            if (editingId) {
                await api.put(`/texts/${editingId}`, form);
            } else {
                await api.post("/texts", form);
            }
            setForm(emptyForm);
            setEditingId(null);
            await loadTexts();
        } catch (err) {
            const d = err?.response?.data?.detail;
            setError(typeof d === "string" ? d : "Erro ao salvar.");
        } finally {
            setLoading(false);
        }
    };

    const startEdit = (t) => {
        setEditingId(t.id);
        setForm({
            title: t.title,
            excerpt: t.excerpt || "",
            body: t.body,
            published: t.published,
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const cancelEdit = () => {
        setEditingId(null);
        setForm(emptyForm);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Excluir este texto definitivamente?")) return;
        await api.delete(`/texts/${id}`);
        await loadTexts();
    };

    return (
        <div className="min-h-screen bg-[#0E2018] text-[#EAEAE6]">
            <header className="border-b border-[#1B3025] px-6 py-6 flex items-center justify-between max-w-5xl mx-auto">
                <div>
                    <p className="text-[10px] uppercase tracking-[0.32em] text-[#7E8978] font-sans font-light">
                        Administração
                    </p>
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-1">
                        {[["texts", "Textos"], ["radar", "Radar"], ["science", "Revisão Científica"], ["review", "Revisar site"], ["messages", "Mensagens"]].map(([k, l]) => (
                            <button key={k} type="button" data-testid={`admin-tab-${k}`} onClick={() => setTab(k)} className={`font-serif text-[18px] transition-colors ${tab === k ? "text-[#EAEAE6] border-b border-[#C7A659]" : "text-[#7E8978] hover:text-[#EAEAE6]"}`}>
                                {l}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="flex items-center gap-6">
                    <Link
                        to="/"
                        className="font-sans font-light text-[12px] tracking-[0.06em] uppercase text-[#B0B6A6] hover:text-[#EAEAE6]"
                    >
                        Ver site →
                    </Link>
                    <button
                        type="button"
                        data-testid={ADMIN.logout}
                        onClick={handleLogout}
                        className="font-sans font-light text-[12px] tracking-[0.06em] uppercase text-[#B0B6A6] hover:text-[#EAEAE6]"
                    >
                        Sair
                    </button>
                </div>
            </header>

            {user && tab === "radar" ? <AdminRadar /> : user && tab === "messages" ? <AdminMessages /> : user && tab === "science" ? <AdminScience /> : user && tab === "review" ? <AdminSiteReview /> : (
            <main className="max-w-3xl mx-auto px-6 py-14">
                <form
                    onSubmit={handleSubmit}
                    className="flex flex-col gap-6 mb-20 border-b border-[#1B3025] pb-14"
                >
                    <p className="text-[10px] uppercase tracking-[0.28em] text-[#7E8978] font-sans">
                        {editingId ? "Editando texto" : "Novo texto"}
                    </p>
                    <label className="flex flex-col gap-2">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[#7E8978] font-sans">
                            título
                        </span>
                        <input
                            type="text"
                            required
                            data-testid={ADMIN.newTitle}
                            value={form.title}
                            onChange={(e) =>
                                setForm({ ...form, title: e.target.value })
                            }
                            className="bg-transparent border-b border-[#234135] focus:border-[#EAEAE6] outline-none px-1 py-2 text-[#EAEAE6] font-serif text-[20px]"
                        />
                    </label>
                    <label className="flex flex-col gap-2">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[#7E8978] font-sans">
                            resumo (opcional)
                        </span>
                        <input
                            type="text"
                            data-testid={ADMIN.newExcerpt}
                            value={form.excerpt}
                            onChange={(e) =>
                                setForm({ ...form, excerpt: e.target.value })
                            }
                            className="bg-transparent border-b border-[#234135] focus:border-[#EAEAE6] outline-none px-1 py-2 text-[#B0B6A6] font-sans italic"
                        />
                    </label>
                    <label className="flex flex-col gap-2">
                        <span className="text-[10px] uppercase tracking-[0.2em] text-[#7E8978] font-sans">
                            corpo do texto
                        </span>
                        <textarea
                            required
                            data-testid={ADMIN.newBody}
                            value={form.body}
                            rows={10}
                            onChange={(e) =>
                                setForm({ ...form, body: e.target.value })
                            }
                            className="bg-transparent border border-[#234135] focus:border-[#EAEAE6] outline-none p-4 text-[#EAEAE6] font-serif text-[16px] leading-relaxed"
                        />
                    </label>
                    <label className="flex items-center gap-3 text-[12px] font-sans text-[#B0B6A6]">
                        <input
                            type="checkbox"
                            checked={form.published}
                            onChange={(e) =>
                                setForm({ ...form, published: e.target.checked })
                            }
                        />
                        publicar (visível no site)
                    </label>
                    {error && (
                        <p className="text-[12px] font-sans text-[#B0B6A6]">{error}</p>
                    )}
                    <div className="flex items-center gap-6">
                        <button
                            type="submit"
                            data-testid={editingId ? ADMIN.saveEdit : ADMIN.newSubmit}
                            disabled={loading}
                            className="inline-flex items-center gap-3 font-sans font-light text-[13px] tracking-[0.06em] uppercase text-[#EAEAE6] border-b border-[#234135] hover:border-[#EAEAE6] pb-2 transition-colors disabled:opacity-40"
                        >
                            {loading
                                ? "Salvando…"
                                : editingId
                                  ? "Salvar alterações →"
                                  : "Publicar texto →"}
                        </button>
                        {editingId && (
                            <button
                                type="button"
                                data-testid={ADMIN.cancelEdit}
                                onClick={cancelEdit}
                                className="font-sans font-light text-[12px] text-[#7E8978] hover:text-[#EAEAE6]"
                            >
                                cancelar
                            </button>
                        )}
                    </div>
                </form>

                <section data-testid={ADMIN.list}>
                    <p className="text-[10px] uppercase tracking-[0.32em] text-[#7E8978] font-sans font-light mb-8">
                        Textos publicados ({texts.length})
                    </p>
                    <ul className="flex flex-col">
                        {texts.map((t) => (
                            <li
                                key={t.id}
                                data-testid={ADMIN.item(t.id)}
                                className="border-b border-[#1B3025] py-6 flex items-start justify-between gap-6"
                            >
                                <div className="flex-1">
                                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#7E8978] font-sans mb-2">
                                        {new Date(t.created_at).toLocaleDateString(
                                            "pt-BR"
                                        )}{" "}
                                        · {t.published ? "publicado" : "rascunho"}
                                    </p>
                                    <h3 className="font-serif text-[20px] text-[#EAEAE6]">
                                        {t.title}
                                    </h3>
                                    {t.excerpt && (
                                        <p className="mt-1 font-sans font-light text-[12.5px] italic text-[#B0B6A6]">
                                            {t.excerpt}
                                        </p>
                                    )}
                                </div>
                                <div className="flex flex-col items-end gap-2">
                                    <button
                                        type="button"
                                        data-testid={ADMIN.editBtn(t.id)}
                                        onClick={() => startEdit(t)}
                                        className="font-sans font-light text-[11px] uppercase tracking-[0.1em] text-[#B0B6A6] hover:text-[#EAEAE6]"
                                    >
                                        editar
                                    </button>
                                    <button
                                        type="button"
                                        data-testid={ADMIN.deleteBtn(t.id)}
                                        onClick={() => handleDelete(t.id)}
                                        className="font-sans font-light text-[11px] uppercase tracking-[0.1em] text-[#7E8978] hover:text-[#EAEAE6]"
                                    >
                                        excluir
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </section>
            </main>
            )}
        </div>
    );
};

export default AdminDashboard;
